/* eslint-disable max-params, max-lines, default-param-last, no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-inferrable-types, @typescript-eslint/no-unused-vars, class-methods-use-this */
import { Object3D, Scene, SpotLight, Vector3 } from 'three';

import { AnimationManager } from '../../animation/animation-manager.class';

import { HighlightCircleConfig } from '../arc-calculation';
import { Highlight, HighlightConfig } from '../highlight.class';
import {
	highlightCfn,
	clearHighlightCfn
} from './functions';

/**
 * Animation phases for highlight transitions
 */
export type HighlightAnimationPhase = 'sliding-forward' | 'backing-up' | 'arc-transition' | null;

/**
 * Configuration for the HighlightManager
 */
export interface HighlightManagerConfig {
	/** Scene to add objects to */
	scene: Scene;
	/** Animation manager for coordinating animations */
	animationManager: AnimationManager;
	/** Initial circle configuration */
	circle?: HighlightCircleConfig;
	/** Default animation duration in ms */
	animationDuration?: number;
	/** Maximum spotlight intensity */
	maxIntensity?: number;
	/** Initial debug mode state */
	debugMode?: boolean;
	/** Callback when first highlight is added (0 -> 1 highlights) */
	onFirstHighlightAdded?: () => void;
	/** Callback when last highlight is removed (1 -> 0 highlights) */
	onLastHighlightRemoved?: () => void;
}

/**
 * Manages multiple highlights and coordinates animations between them
 *
 * Features:
 * - Tracks multiple highlights
 * - Coordinates transitions between highlights
 * - Manages spotlight animations (position, intensity)
 * - Provides arc-based transitions for visual continuity
 */
export class HighlightManager {
	#scene: Scene;
	#animationManager: AnimationManager;
	#highlights: Map<string, Highlight> = new Map();
	#currentHighlightId: string | null = null;
	#previousHighlightId: string | null = null;
	#debugMode = false;

	/** Circle configuration for light positioning */
	#circle: HighlightCircleConfig | null = null;

	/** Animation settings */
	#animationDuration: number;
	#maxIntensity: number;

	/** Current animation state */
	#animationPhase: HighlightAnimationPhase = null;
	#arcPoints: Vector3[] = [];
	#arcMidpoint: Vector3 | null = null;

	/** Callbacks for highlight state changes */
	#onFirstHighlightAdded?: () => void;
	#onLastHighlightRemoved?: () => void;

	constructor (config: HighlightManagerConfig) {
		this.#scene = config.scene;
		this.#animationManager = config.animationManager;
		this.#circle = config.circle ?? null;
		this.#animationDuration = config.animationDuration ?? 1000;
		this.#maxIntensity = config.maxIntensity ?? 1000;
		this.#debugMode = config.debugMode ?? false;
		this.#onFirstHighlightAdded = config.onFirstHighlightAdded;
		this.#onLastHighlightRemoved = config.onLastHighlightRemoved;
	}

	// Public getters for cfn function access
	get scene (): Scene { return this.#scene; }
	get animationManager (): AnimationManager { return this.#animationManager; }
	get highlights (): Map<string, Highlight> { return this.#highlights; }
	get currentHighlightId (): string | null { return this.#currentHighlightId; }
	set currentHighlightId (value: string | null) { this.#currentHighlightId = value; }
	get previousHighlightId (): string | null { return this.#previousHighlightId; }
	set previousHighlightId (value: string | null) { this.#previousHighlightId = value; }
	get circle (): HighlightCircleConfig | null { return this.#circle; }
	get animationDuration (): number { return this.#animationDuration; }
	get maxIntensity (): number { return this.#maxIntensity; }
	get animationPhase (): HighlightAnimationPhase { return this.#animationPhase; }
	set animationPhase (value: HighlightAnimationPhase) { this.#animationPhase = value; }
	get arcPoints (): Vector3[] { return this.#arcPoints; }
	set arcPoints (value: Vector3[]) { this.#arcPoints = value; }
	get arcMidpoint (): Vector3 | null { return this.#arcMidpoint; }
	set arcMidpoint (value: Vector3 | null) { this.#arcMidpoint = value; }
	get onFirstHighlightAdded (): (() => void) | undefined { return this.#onFirstHighlightAdded; }
	get onLastHighlightRemoved (): (() => void) | undefined { return this.#onLastHighlightRemoved; }

	/** Get current highlight */
	get currentHighlight (): Highlight | null {
		if (!this.currentHighlightId) return null;
		return this.highlights.get(this.currentHighlightId) ?? null;
	}

	/** Get previous highlight (for transitions) */
	get previousHighlight (): Highlight | null {
		if (!this.previousHighlightId) return null;
		return this.highlights.get(this.previousHighlightId) ?? null;
	}

	/** Get debug mode */
	get debugMode (): boolean {
		return this.#debugMode;
	}

	/** Get current animation phase */
	get currentAnimationPhase (): HighlightAnimationPhase {
		return this.animationPhase;
	}

	/** Get all highlight IDs */
	getAllHighlightIds (): string[] {
		return Array.from(this.highlights.keys());
	}

	/** Get all highlights */
	getAllHighlights (): Highlight[] {
		return Array.from(this.highlights.values());
	}

	/** Get highlight count */
	get highlightCount (): number {
		return this.highlights.size;
	}

	/** Check if a specific highlight exists */
	hasHighlight (objectId: string): boolean {
		return this.highlights.has(objectId);
	}

	/**
	 * Set circle configuration
	 */
	/**
	 * Re-aim an existing highlight at its object's current position (a dragged
	 * appliance). Stops the fly-in position tween so the two do not fight.
	 * @returns false when the object is not highlighted
	 */
	followHighlight (objectId: string): boolean {
		const highlight = this.#highlights.get(String(objectId));
		if (!highlight) return false;
		this.#animationManager.stop(`highlight-position-${highlight.objectId}`);
		highlight.follow();
		return true;
	}

	setCircle (circle: HighlightCircleConfig): void {
		this.#circle = circle;
	}

	/**
	 * Check if circle is configured
	 */
	hasCircle (): boolean {
		return this.circle !== null;
	}

	/**
	 * Set debug mode for all highlights
	 */
	setDebugMode (enabled: boolean): void {
		this.#debugMode = enabled;
		for (const highlight of this.highlights.values()) {
			highlight.setDebugMode(enabled);
		}
	}

	/**
	 * Highlight an object
	 *
	 * @param objectId Unique identifier for the object
	 * @param object The Three.js object to highlight
	 * @param addHighlight If true, adds a new highlight keeping existing ones.
	 *                     If false (default), switches to the new highlight (moves the light).
	 * @param spotlightConfig Optional spotlight configuration
	 */
	highlight (
		objectId: string,
		object: Object3D,
		addHighlight = false,
		spotlightConfig?: HighlightConfig['spotlight']
	): void {
		highlightCfn(this, { objectId, object, addHighlight, spotlightConfig });
	}

	/**
	 * Clear the current highlight with animation
	 */
	clearHighlight (objectId?: string): void {
		clearHighlightCfn(this, { objectId });
	}

	/**
	 * Clear all highlights
	 */
	clear (): void {
		const hadHighlights = this.highlights.size > 0;

		// Stop all animations
		for (const highlight of this.highlights.values()) {
			this.animationManager.stop(`highlight-position-${highlight.objectId}`);
			this.animationManager.stop(`highlight-intensity-${highlight.objectId}`);
			highlight.dispose();
		}

		this.highlights.clear();
		this.currentHighlightId = null;
		this.previousHighlightId = null;
		this.animationPhase = null;

		// Trigger callback if we had highlights and now have none
		if (hadHighlights) {
			this.onLastHighlightRemoved?.();
		}
		this.arcPoints = [];
		this.arcMidpoint = null;
	}

	/**
	 * Get interpolated position for current animation
	 * Used when external code needs to know the animated position
	 */
	getAnimatedPosition (): Vector3 | null {
		const highlight = this.currentHighlight;
		if (!highlight?.light) return null;

		return highlight.light.position.clone();
	}

	/**
	 * Check if any animation is in progress
	 */
	isAnimating (): boolean {
		return this.animationPhase !== null;
	}

	/**
	 * Update method - call this in the render loop
	 * Note: AnimationManager.update() should also be called separately
	 */
	update (): void {
		// This method can be used for any per-frame updates
		// that aren't handled by the animation manager
	}

	/**
	 * Dispose of all resources
	 */
	dispose (): void {
		this.clear();
	}
}
